import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import { useEffect, useRef, useState } from 'react';
import { Box, TextField, InputAdornment, useTheme, useMediaQuery, IconButton } from '@mui/material';
import { CloseCircle, SearchNormal } from 'iconsax-react';
import { Iconly } from 'react-iconly';
import { between } from '@ui/style/style';
import { Icon } from '@iconify/react';
const SearchFilter = ({ searchValue, onSearchChange, placeholder = 'Search...', onFilterClick, filterLabel = 'Filters', breakpoint = 'lg', onClear, isDatagrid = false, mobileSearchOpen: controlledMobileSearchOpen, setMobileSearchOpen: setControlledMobileSearchOpen, handleFilterClear, showFilterClearButton, widthBox, positonTop, fullWidth = false, bgVariant = 'white', forceIconVariant = false, forceDesktopVariant, heightBox, iconButtonSize }) => {
    const theme = useTheme();
    const isSmallScreen = useMediaQuery(theme.breakpoints.down(breakpoint));
    const useIconVariant = !forceDesktopVariant && (forceIconVariant || isSmallScreen);
    const isControlled = typeof controlledMobileSearchOpen === 'boolean' &&
        typeof setControlledMobileSearchOpen === 'function';
    const [uncontrolledMobileSearchOpen, setUncontrolledMobileSearchOpen] = useState(false);
    const mobileSearchOpen = isControlled ? controlledMobileSearchOpen : uncontrolledMobileSearchOpen;
    const setMobileSearchOpen = isControlled
        ? setControlledMobileSearchOpen
        : setUncontrolledMobileSearchOpen;
    const [internalSearch, setInternalSearch] = useState(searchValue);
    const debounceRef = useRef(null);
    useEffect(() => {
        setInternalSearch(searchValue);
    }, [searchValue]);
    const handleSearchChange = (newValue) => {
        const prevLength = (internalSearch ?? '').length;
        setInternalSearch(newValue);
        if (debounceRef.current) {
            clearTimeout(debounceRef.current);
        }
        debounceRef.current = setTimeout(() => {
            if (newValue.length >= 3 || newValue.length === 0) {
                if (onSearchChange) {
                    onSearchChange(newValue);
                }
            }
        }, 500);
    };
    const handleMobileToggle = () => {
        if (!mobileSearchOpen && internalSearch) {
            setInternalSearch('');
            if (onSearchChange) {
                onSearchChange('');
            }
            if (onClear) {
                onClear();
            }
        }
        setMobileSearchOpen(!mobileSearchOpen);
    };
    const handleClear = () => {
        setInternalSearch('');
        if (onSearchChange) {
            onSearchChange('');
        }
        if (onClear) {
            onClear();
        }
        if (useIconVariant) {
            setMobileSearchOpen(false);
        }
    };
    return (_jsx(_Fragment, { children: !useIconVariant ? (_jsxs(Box, { sx: {
                display: 'flex',
                alignItems: 'center',
                gap: 0.5,
                overflow: 'hidden',
                pt: '2px',
                width: fullWidth ? '100%' : undefined
            }, children: [_jsx(TextField, { value: internalSearch, onChange: (e) => handleSearchChange(e.target.value), placeholder: placeholder, variant: 'outlined', hiddenLabel: true, size: isSmallScreen ? 'small' : 'small', fullWidth: fullWidth, sx: {
                        bgcolor: bgVariant === 'grey8' ? theme.palette.common.grey8 : 'transparent',
                        color: bgVariant === 'grey8' ? theme.palette.text.primary : undefined,
                        minWidth: isSmallScreen ? 100 : 180,
                        '& .MuiFormControl-root-MuiTextField-root': {
                            borderRadius: '16px'
                        },
                        '& .MuiInputBase-input-MuiOutlinedInput-input': {
                            color: bgVariant === 'grey8' ? theme.palette.text.primary : undefined
                        },
                        '& .MuiOutlinedInput-root': {
                            height: '40px',
                            display: 'flex',
                            alignItems: 'center'
                        }
                    }, InputProps: {
                        startAdornment: (_jsx(InputAdornment, { position: 'start', children: _jsx(SearchNormal, { variant: 'Outline', size: 24, color: theme.palette.text.secondary }) })),
                        endAdornment: internalSearch && (_jsx(InputAdornment, { position: 'end', children: _jsx(CloseCircle, { style: {
                                    cursor: 'pointer'
                                }, size: 16, color: theme.palette.text.secondary, onClick: handleClear }) }))
                    } }), (onFilterClick || showFilterClearButton) && (_jsxs(_Fragment, { children: [showFilterClearButton && (_jsx(IconButton, { onClick: handleFilterClear, size: 'medium', color: 'default', 
                            // sx={{ border: 'none' }}
                            disableFocusRipple: true, disableRipple: true, children: _jsx(Icon, { icon: 'eva:close-fill' }) })), onFilterClick && (_jsx(IconButton, { size: isSmallScreen ? 'small' : 'medium', color: 'default', onClick: onFilterClick, children: _jsx(Iconly, { name: 'Filter', set: 'curved', stroke: 'light', style: { width: 20, height: 20 }, primaryColor: theme.palette.grey[900] }) }))] }))] })) : (_jsxs(Box, { display: 'flex', gap: 0.5, position: 'relative', width: widthBox && mobileSearchOpen ? widthBox : undefined, children: [_jsx(IconButton, { size: iconButtonSize ?? 'small', color: 'inherit', onClick: handleMobileToggle, children: _jsx(SearchNormal, { size: 20, variant: 'Outline' }) }), showFilterClearButton && (_jsx(IconButton, { size: iconButtonSize ?? 'small', color: 'inherit', onClick: handleFilterClear, disableFocusRipple: true, disableRipple: true, children: _jsx(Icon, { icon: 'eva:close-fill' }) })), onFilterClick && (_jsx(IconButton, { size: iconButtonSize ?? 'small', color: 'inherit', onClick: onFilterClick, children: _jsx(Iconly, { name: 'Filter', set: 'curved', style: { width: 20, height: 20 } }) })), mobileSearchOpen && (_jsxs(Box, { sx: {
                        position: isDatagrid ? 'absolute' : 'fixed',
                        top: positonTop ? positonTop : 0,
                        right: 0,
                        left: 0,
                        zIndex: 1000,
                        height: heightBox ? heightBox : 54,
                        backdropFilter: 'blur(20px)',
                        backgroundColor: theme.palette.common.white92,
                        px: 2,
                        gap: 1,
                        ...between
                    }, children: [_jsx(TextField, { fullWidth: true, value: internalSearch, onChange: (e) => handleSearchChange(e.target.value), variant: 'outlined', size: 'small', autoFocus: true, hiddenLabel: true, sx: {
                                bgcolor: bgVariant === 'grey8' ? theme.palette.common.grey8 : 'transparent',
                                color: bgVariant === 'grey8' ? theme.palette.text.primary : undefined,
                                '& .MuiFormControl-root-MuiTextField-root': {
                                    borderRadius: '16px'
                                },
                                '& .MuiInputBase-input-MuiOutlinedInput-input': {
                                    color: bgVariant === 'grey8' ? theme.palette.text.primary : undefined
                                }
                            }, InputProps: {
                                startAdornment: (_jsx(InputAdornment, { position: 'start', children: _jsx(SearchNormal, { size: 20, variant: 'Outline', color: theme.palette.text.secondary }) }))
                            } }), _jsx(Icon, { color: theme.palette.text.primary, style: {
                                cursor: 'pointer'
                            }, icon: 'iconamoon:close-bold', onClick: handleClear })] }))] })) }));
};
export default SearchFilter;
